/*
Copyright (C) 2017  Cloudbase Solutions SRL
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { Link } from "react-router";
import {
  ArrowRight,
  ArrowUpRight,
  Bot,
  CalendarClock,
  CircleCheck,
  Cloud,
  Gauge,
  HardDrive,
  History,
  Route,
  Rocket,
  ArrowRightLeft,
  ShieldCheck,
} from "lucide-react";

import MigrationFlow from "@src/components/modules/LiveModule/MigrationFlow";
import Sparkline from "@src/components/modules/LiveModule/Sparkline";
import {
  Bar,
  Card,
  CardBody,
  CardHeader,
  Chip,
  EmptyNote,
  Kpi,
  KpiGrid,
  TextLink,
} from "@src/components/modules/LiveModule/ui/LiveUi";
import type { LiveEntry } from "@src/components/modules/LiveModule/LiveStore";
import {
  LIVE,
  TONE_COLOURS,
  endpointOf,
  formatAgo,
  formatClock,
  phaseTone,
  targetFill,
} from "@src/components/modules/LiveModule/liveUtils";
import { Donut, STATUS_COLOURS, StackedColumns } from "@src/components/modules/DashboardModule/DashboardCharts";
import type { Estate } from "@src/components/modules/DashboardModule/dashboardData";
import { formatBytes, formatDuration, formatRate, GIB } from "@src/utils/TransferProgress";

/*
 * The management dashboard, laid out like the Ops Console consolidated dashboard: "Business at a
 * glance" KPIs, what needs attention, what is live now, then the estate's charts and lists.
 */

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  padding-bottom: 48px;
  .span-2 {
    grid-column: span 2;
  }
  .span-3 {
    grid-column: 1 / -1;
  }
  @media (max-width: 1150px) {
    grid-template-columns: minmax(0, 1fr);
    .span-2,
    .span-3 {
      grid-column: auto;
    }
  }
`;
const Headline = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
  .n {
    font-size: 28px;
    line-height: 34px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${LIVE.text};
    font-variant-numeric: tabular-nums;
  }
  .u {
    font-size: 13px;
    color: ${LIVE.muted};
  }
`;
const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;
const AttentionRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${LIVE.warnBorder};
  &:last-child {
    border-bottom: none;
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-top: 6px;
    flex-shrink: 0;
  }
  .t {
    flex-grow: 1;
    min-width: 0;
    font-size: 13px;
    color: ${LIVE.text};
    line-height: 19px;
  }
  .d {
    display: block;
    font-size: 12px;
    color: ${LIVE.muted};
  }
`;
const SmallButton = styled(Link)`
  flex-shrink: 0;
  height: 26px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--t9-border-control);
  border-radius: 6px;
  background: var(--t9-bg-surface);
  font-size: 12px;
  font-weight: 600;
  color: ${LIVE.slate};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    border-color: ${LIVE.slate};
  }
`;
const Ok = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${LIVE.textSecondary};
  padding: 8px 0;
`;
const LiveRow = styled(Link)`
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--t9-bg-sunken);
  text-decoration: none;
  color: inherit;
  &:last-child {
    border-bottom: none;
  }
  /* A phone: the drawing above the details, so the row fits the screen. */
  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    > :first-child {
      max-width: 200px;
    }
  }
  &:hover .name {
    text-decoration: underline;
  }
  .top {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
  .name {
    font-size: 13.5px;
    font-weight: 600;
    color: ${LIVE.text};
    flex-grow: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .route {
    margin-top: 2px;
    font-size: 12px;
    color: ${LIVE.muted};
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .figs {
    margin-top: 6px;
    font-size: 12px;
    color: ${LIVE.muted};
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    b {
      color: ${LIVE.text};
      font-weight: 600;
    }
  }
`;
const RouteRow = styled.li`
  padding: 8px 0;
  border-bottom: 1px solid var(--t9-bg-sunken);
  &:last-child {
    border-bottom: none;
  }
  .top {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${LIVE.text};
    font-weight: 600;
    margin-bottom: 6px;
  }
  .top .n {
    margin-left: auto;
    font-weight: 500;
    color: ${LIVE.muted};
    font-size: 12px;
    b {
      color: ${LIVE.text};
      font-weight: 600;
    }
  }
`;
const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid var(--t9-bg-sunken);
  font-size: 13px;
  &:last-child {
    border-bottom: none;
  }
  .main {
    flex-grow: 1;
    min-width: 0;
  }
  .main a,
  .main .t {
    display: block;
    color: ${LIVE.text};
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .main a:hover {
    text-decoration: underline;
  }
  .sub {
    font-size: 12px;
    color: ${LIVE.muted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .val {
    font-weight: 600;
    color: ${LIVE.text};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    text-align: right;
  }
  .when {
    font-size: 12px;
    color: ${LIVE.muted};
    white-space: nowrap;
  }
`;
const Rank = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: ${LIVE.track};
  color: ${LIVE.slate};
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const Dot = styled.span<{ $c: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$c};
  flex-shrink: 0;
`;

type Props = {
  estate: Estate;
  loaded: boolean;
  live: LiveEntry[];
  finishedToday: number;
  now: number;
};

const icon = (I: any) => <I size={16} color={LIVE.faint} strokeWidth={1.9} aria-hidden="true" />;
const pct = (n: number | null) => (n == null ? "—" : `${Math.round(n)}%`);
const shortDay = (t: number) => new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const DashboardContent = observer(({ estate, loaded, live, finishedToday, now }: Props) => {
  const e = estate;
  const topProviders = e.endpoints.byProvider
    .slice(0, 3)
    .map(p => `${p.count} ${p.label}`)
    .join(" · ");
  const dataValue = e.data.bytes30 > 0 ? formatBytes(e.data.bytes30) : String(e.data.machinesMigrated);
  const dayLabels = e.days.map(d => shortDay(d.day));
  const routeMax = Math.max(1, ...e.routes.map(r => r.machines));
  const topMax = Math.max(1, ...e.top.map(t => t.bytes));
  const tinted = e.attention.length > 0;

  return (
    <Wrapper>
      <Card className="span-3">
        <CardHeader title="Business at a glance" note="Last 30 days" />
        <CardBody>
          <KpiGrid $min={170}>
            <Kpi
              label="Transfers"
              value={e.transfers.total}
              icon={icon(ArrowRightLeft)}
              to="/transfers"
              loading={!loaded}
              sub={
                <>
                  <b>{e.transfers.active}</b> active · {e.transfers.replicas} replicas · {e.transfers.migrations} migrations
                </>
              }
              spark={<Sparkline values={e.transfers.trend} step colour={LIVE.slate} />}
            />
            <Kpi
              label="Deployments"
              value={e.deployments.last30}
              icon={icon(Rocket)}
              to="/deployments"
              loading={!loaded}
              sub={
                <>
                  <b>{e.deployments.completed}</b> completed · {e.deployments.failed} failed
                </>
              }
              spark={<Sparkline values={e.deployments.perWeek} colour={LIVE.slate} />}
            />
            <Kpi
              label="Data migrated"
              value={dataValue}
              unit={e.data.bytes30 > 0 ? undefined : "machines"}
              icon={icon(HardDrive)}
              loading={!loaded}
              sub={e.data.bytes30 > 0 ? `copied by ${e.data.syncs30} syncs` : "migrated so far"}
              spark={e.data.bytes30 > 0 ? <Sparkline values={e.data.perDay} colour={LIVE.cyanDeep} /> : undefined}
            />
            <Kpi
              label="Success rate"
              value={pct(e.success.rate)}
              icon={icon(ShieldCheck)}
              loading={!loaded}
              tone={e.success.rate != null && e.success.rate < 90 ? "warning" : null}
              sub={
                <>
                  <b>{e.success.completed}</b> completed · {e.success.failed} failed
                </>
              }
              spark={<Sparkline values={e.success.perWeek.map(v => (v == null ? 100 : v))} colour={LIVE.greenDeep} />}
            />
            <Kpi
              label="Cloud endpoints"
              value={e.endpoints.total}
              icon={icon(Cloud)}
              to="/endpoints"
              loading={!loaded}
              sub={topProviders || "None yet"}
            />
            <Kpi
              label="SwiftIQ Agents"
              value={e.agents.allocated}
              unit={`of ${e.agents.pools} allocated`}
              icon={icon(Bot)}
              to="/minion-pools"
              loading={!loaded}
              tone={e.agents.errors.length ? "warning" : null}
              sub={
                e.agents.errors.length ? (
                  <>
                    <b>{e.agents.errors.length}</b> in error · {e.agents.inUse} of {e.agents.machines} machines busy
                  </>
                ) : (
                  `${e.agents.inUse} of ${e.agents.machines} machines busy`
                )
              }
            />
          </KpiGrid>
        </CardBody>
      </Card>

      <Card $tint={tinted ? "warning" : null}>
        <CardHeader
          title="Needs attention"
          tint={tinted}
          note={tinted ? `${e.attention.length} item${e.attention.length === 1 ? "" : "s"}` : undefined}
        />
        <CardBody>
          {!loaded ? (
            <EmptyNote>Checking…</EmptyNote>
          ) : e.attention.length ? (
            <List>
              {e.attention.slice(0, 5).map(a => (
                <AttentionRow key={a.text + a.link}>
                  <span
                    className="dot"
                    style={{
                      background: a.severity === "critical" ? LIVE.danger : a.severity === "warning" ? LIVE.warning : LIVE.faint,
                    }}
                    aria-label={a.severity}
                  />
                  <span className="t">
                    {a.text}
                    {a.detail ? <span className="d">{a.detail}</span> : null}
                  </span>
                  <SmallButton to={a.link}>{a.action}</SmallButton>
                </AttentionRow>
              ))}
              {e.attention.length > 5 ? (
                <AttentionRow>
                  <span className="t">
                    <span className="d">and {e.attention.length - 5} more</span>
                  </span>
                </AttentionRow>
              ) : null}
            </List>
          ) : (
            <Ok>
              <CircleCheck size={18} color={LIVE.greenDeep} />
              Nothing needs attention right now.
            </Ok>
          )}
        </CardBody>
      </Card>

      <Card className="span-2">
        <CardHeader
          title={
            <>
              Live now
              {live.length ? (
                <Chip tone="info" pulse>
                  {live.length} running
                </Chip>
              ) : null}
            </>
          }
          note={
            <TextLink to="/live">
              Open live monitor <ArrowUpRight size={14} />
            </TextLink>
          }
        />
        <CardBody>
          {live.length ? (
            live.slice(0, 4).map(entry => {
              const p = entry.progress;
              const src = endpointOf(entry.sourceEndpointId);
              const dst = endpointOf(entry.targetEndpointId);
              return (
                <LiveRow key={entry.key} to={entry.link}>
                  <MigrationFlow
                    size="small"
                    sourceType={src?.type}
                    targetType={dst?.type}
                    phase={p.phase}
                    percent={targetFill(p)}
                    rateBytesPerSec={p.rateBytesPerSec}
                    activeDisks={p.activeDisks}
                  />
                  <div>
                    <div className="top">
                      <span className="name">{entry.name}</span>
                      <Chip tone={phaseTone(p.phase)}>{p.phase}</Chip>
                    </div>
                    <Bar percent={p.percent} height={6} moving={p.rateBytesPerSec > 0} label={`${entry.name} progress`} />
                    <div className="figs">
                      <b>{Math.floor(p.percent)}%</b>
                      {p.hasDiskData ? (
                        <span>
                          · {formatBytes(p.doneBytes)} of {formatBytes(p.totalBytes)}
                        </span>
                      ) : null}
                      {p.rateBytesPerSec > 0 ? <span>· {formatRate(p.rateBytesPerSec)}</span> : null}
                      {p.etaSeconds != null && p.etaSeconds > 0 ? (
                        <span>
                          · ~{formatDuration(p.etaSeconds)} left, about {formatClock(now + p.etaSeconds * 1000, now)}
                        </span>
                      ) : null}
                    </div>
                    <div className="route">
                      {src?.name || "source"} <ArrowRight size={11} /> {dst?.name || "target"}
                    </div>
                  </div>
                </LiveRow>
              );
            })
          ) : (
            <Ok>
              <Gauge size={18} color={LIVE.faint} />
              Nothing is moving right now.
              {finishedToday ? ` ${finishedToday} run${finishedToday === 1 ? "" : "s"} finished in the last 24 hours.` : ""}
            </Ok>
          )}
          {live.length > 4 ? (
            <div style={{ marginTop: 8 }}>
              <TextLink to="/live">and {live.length - 4} more in the live monitor</TextLink>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card className="span-2">
        <CardHeader title="Executions per day" note="Syncs, migration runs and deployments · 30 days" />
        <CardBody>
          <StackedColumns
            ariaLabel="Executions per day, last 30 days, by outcome"
            labels={dayLabels}
            series={[
              { key: "completed", label: "Completed", colour: STATUS_COLOURS.completed },
              { key: "running", label: "Running", colour: STATUS_COLOURS.running },
              { key: "failed", label: "Failed", colour: STATUS_COLOURS.failed },
            ]}
            values={{
              completed: e.days.map(d => d.completed),
              running: e.days.map(d => d.running),
              failed: e.days.map(d => d.failed),
            }}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Endpoints by provider" note={`${e.endpoints.total} cloud endpoints`} />
        <CardBody>
          {e.endpoints.total ? (
            <Donut
              ariaLabel="Cloud endpoints by provider"
              items={e.endpoints.byProvider.map(p => ({ label: p.label, value: p.count }))}
              centre={String(e.endpoints.total)}
              centreLabel="endpoints"
            />
          ) : (
            <EmptyNote>No cloud endpoints yet.</EmptyNote>
          )}
        </CardBody>
      </Card>

      <Card className="span-2">
        <CardHeader title="Data moved per day" note="Bytes copied by finished syncs · 30 days" />
        <CardBody>
          <Headline>
            <span className="n">{formatBytes(e.data.bytes30)}</span>
            <span className="u">in 30 days · {formatBytes(e.days[e.days.length - 1]?.bytes || 0)} today</span>
          </Headline>
          <StackedColumns
            ariaLabel="Data copied per day, last 30 days"
            labels={dayLabels}
            series={[{ key: "bytes", label: "Copied", colour: LIVE.cyanDeep }]}
            values={{ bytes: e.days.map(d => d.bytes / GIB) }}
            formatValue={v => (v >= 1024 ? `${(v / 1024).toFixed(1)} TiB` : `${Math.round(v)} GiB`)}
            height={150}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Migrations by route" icon={<Route size={16} color={LIVE.faint} />} note="Machines per route" />
        <CardBody>
          {e.routes.length ? (
            <List>
              {e.routes.slice(0, 6).map(r => (
                <RouteRow key={`${r.source}-${r.target}`}>
                  <div className="top">
                    <span>{r.source}</span>
                    <ArrowRight size={13} color={LIVE.faint} aria-label="to" />
                    <span>{r.target}</span>
                    <span className="n">
                      <b>{r.machines}</b> machine{r.machines === 1 ? "" : "s"} · {r.transfers} transfer{r.transfers === 1 ? "" : "s"}
                    </span>
                  </div>
                  <Bar percent={(r.machines / routeMax) * 100} tone="brand" height={6} label={`${r.source} to ${r.target}`} />
                </RouteRow>
              ))}
            </List>
          ) : (
            <EmptyNote>No transfers yet.</EmptyNote>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Top transfers by size" icon={<HardDrive size={16} color={LIVE.faint} />} note="Source disks" />
        <CardBody>
          {e.top.length ? (
            <List>
              {e.top.map((t, i) => (
                <Row key={t.id}>
                  <Rank>{i + 1}</Rank>
                  <div className="main">
                    <Link to={`/transfers/${t.id}`}>{t.name}</Link>
                    <div className="sub">
                      {t.kind} · {t.source} → {t.target}
                    </div>
                    <div style={{ marginTop: 5 }}>
                      <Bar percent={(t.bytes / topMax) * 100} height={4} tone="info" />
                    </div>
                  </div>
                  <span className="val">{formatBytes(t.bytes)}</span>
                </Row>
              ))}
            </List>
          ) : (
            <EmptyNote>Sizes show once transfers have read their machines.</EmptyNote>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Upcoming schedules" icon={<CalendarClock size={16} color={LIVE.faint} />} note="Next 7 days" />
        <CardBody>
          {e.upcoming.length ? (
            <List>
              {e.upcoming.map(u => (
                <Row key={`${u.transferId}-${u.at}`}>
                  <div className="main">
                    <Link to={`/transfers/${u.transferId}/schedule`}>{u.name}</Link>
                    <div className="sub">{u.what}</div>
                  </div>
                  <span className="val">{formatClock(u.at, now)}</span>
                </Row>
              ))}
            </List>
          ) : (
            <EmptyNote>No sync is scheduled in the next 7 days.</EmptyNote>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Recent activity" icon={<History size={16} color={LIVE.faint} />} />
        <CardBody>
          {e.activity.length ? (
            <List>
              {e.activity.map(a => (
                <Row key={`${a.link}-${a.at}-${a.text}`}>
                  <Dot $c={TONE_COLOURS[a.tone].dot} aria-hidden="true" />
                  <div className="main">
                    <Link to={a.link}>{a.text}</Link>
                    <div className="sub">{a.detail}</div>
                  </div>
                  <span className="when">{formatAgo(a.at, now)}</span>
                </Row>
              ))}
            </List>
          ) : (
            <EmptyNote>Nothing has run yet.</EmptyNote>
          )}
        </CardBody>
      </Card>
    </Wrapper>
  );
});

export default DashboardContent;
